import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Check, Heart, Pencil, Trash2 } from "lucide-react";
import type { Product } from "@/lib/googleSheetsService";

interface ProductCardProps {
  product: Product;
  selectedQuantity: number;
  onQuantityChange: (quantity: number) => void;
  onSelect: () => void;
  onToggleFavorite?: (productId: string) => void;
  onEdit?: (product: Product) => void;
  onDelete?: (productId: string) => void;
  showFavoriteIcon?: boolean;
  variant?: "default" | "minimal";
}

export function ProductCard({
  product,
  selectedQuantity,
  onQuantityChange,
  onSelect,
  onToggleFavorite,
  onEdit,
  onDelete,
  showFavoriteIcon = true,
  variant = "default",
}: ProductCardProps) {
  const isSelected = selectedQuantity > 0;

  if (variant === "minimal") {
    return (
      <Card
        className={`p-2 bg-white dark:bg-card border-l-4 ${
          product.productType === "sale"
            ? "border-l-blue-600"
            : product.productType === "expense"
              ? "border-l-pink-600"
              : "border-l-green-600"
        } flex items-center justify-between gap-3 select-none`}
        data-testid={`card-product-minimal-${product.id}`}
      >
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate">{product.name}</h3>
          <p className="text-xs font-bold text-primary">₹{product.salePrice}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              onQuantityChange(Math.max(0, selectedQuantity - 1));
            }}
            data-testid={`button-minus-minimal-${product.id}`}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-4 text-center text-sm font-bold">
            {selectedQuantity}
          </span>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              onQuantityChange(selectedQuantity + 1);
            }}
            data-testid={`button-plus-minimal-${product.id}`}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </Card>
    );
  }

  const borderStyle =
    product.productType === "sale"
      ? "border-l-blue-600"
      : product.productType === "expense"
        ? "border-l-pink-600"
        : "border-l-green-600";

  const isLowStock = product.stock !== undefined && product.lowStockAlert !== undefined && product.stock <= product.lowStockAlert;

  return (
    <Card
      className={`p-3 transition-all cursor-pointer bg-white dark:bg-card border-l-4 ${borderStyle} ${
        isSelected ? "ring-2 ring-primary bg-primary/5" : "hover-elevate"
      } group relative select-none`}
      onClick={() => {
        onSelect();
      }}
      data-testid={`card-product-${product.id}`}
    >
      {isLowStock && (
        <div className="absolute top-1 right-8 z-10">
          <Badge variant="destructive" className="text-[9px] h-4 px-1 animate-pulse">
            Low Stock
          </Badge>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate text-foreground dark:text-foreground">{product.name}</h3>
            {product.category && (
              <Badge variant="secondary" className="text-xs h-4 px-1">
                {product.category}
              </Badge>
            )}
            <div
              className="h-5 w-5 flex items-center justify-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite?.(product.id);
              }}
              data-testid={`button-favorite-${product.id}`}
            >
              <motion.div
                key={product.isFavorite ? "fav" : "unfav"}
                initial={ { scale: 0.8, opacity: 0 } }
                animate={ { scale: 1, opacity: 1 } }
                transition={ { type: "spring", stiffness: 400, damping: 10 } }
              >
                <Heart
                  className={`h-3 w-3 ${product.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground"}`}
                />
              </motion.div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-base font-bold text-primary">
              ₹{product.salePrice}
            </p>
            {product.costPrice > 0 && (
              <p className="text-xs text-muted-foreground">
                Cost: ₹{product.costPrice}
              </p>
            )}
            {product.stock !== undefined && (
              <p className={`text-[11px] font-medium ${isLowStock ? "text-destructive" : "text-muted-foreground"}`}>
                Stock: {product.stock}
              </p>
            )}
          </div>
          <Badge
            variant="secondary"
            className={`text-xs h-4 px-1 mt-0.5 ${
              product.productType === "sale"
                ? "bg-primary/10 text-primary"
                : product.productType === "expense"
                  ? "bg-destructive/10 text-destructive"
                  : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
            }`}
          >
            {product.productType === "both"
              ? "Sale & Expense"
              : product.productType === "sale"
                ? "For Sale"
                : "For Expense"}
          </Badge>
        </div>

        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            {onEdit && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(product);
                }}
                data-testid={`button-edit-${product.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDelete && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(product.id);
                }}
                data-testid={`button-delete-${product.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {isSelected && (
            <div
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={() =>
                  onQuantityChange(Math.max(0, selectedQuantity - 1))
                }
                data-testid={`button-minus-${product.id}`}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <span className="w-6 text-center text-sm font-medium">
                {selectedQuantity}
              </span>
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7"
                onClick={() => onQuantityChange(selectedQuantity + 1)}
                data-testid={`button-plus-${product.id}`}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {isSelected && (
          <div className="absolute top-1 right-1">
            <div className="bg-primary rounded-full p-0.5">
              <Check className="h-2.5 w-2.5 text-primary-foreground" />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
